// Order-level paid add-ons. Renders the "appliesTo: order" upsells that
// aren't part of the dedicated sleeve cross-sell flow (Care kit, Gift box
// + card, Rush production, etc.). Customer ticks any number; each picked
// upsell stacks into the total + emits a visible/hidden cart property so
// the warehouse team can pull the matching item.
//
// Wave 3: the accordion step wrapper (+ its Continue/Skip foot) was
// retired. These controls now render as a flat card inside the
// consolidated "Finish your order" screen (sections/FinishSection.tsx).
// `eligibleOrderUpsells` is exported so the parent can pre-compute the
// same list (for finish_addon_view impressions) and hand it in.

import type { Config, Upsell } from "../types";
import type { Spot } from "../pricing";
import { flowCopy } from "../copy";

// Kinds owned by the sleeve flows (name cross-sell + standalone emoji
// picker). We skip these here so they don't appear in the order-level
// shelf. Phase 91: every `sleeve_emoji_*` kind is excluded by prefix
// (see isSleeveEmojiKind) so newly added emojis never leak in here.
const SLEEVE_OWNED_KINDS = new Set([
  "sleeve_name",
  "sleeve_kid_name_age",
]);
const isSleeveEmojiKind = (kind: string) => kind.startsWith("sleeve_emoji_");

// The exact set of order-level paid add-ons offered for the current
// design. Phase B4: an add-on that declares requiresPlacementSlug only
// shows while that placement exists on this template AND isn't already
// taken by a saved spot - so e.g. a sleeve add-on disappears once sleeve
// embroidery has been placed. (The config loader already drops upsells
// whose required placement is absent, which also covers sleeveless
// garments; the used-slug check is the extra gate the customiser owns.)
export function eligibleOrderUpsells(config: Config, spots: Spot[]): Upsell[] {
  const presentSlugs = new Set(config.placements.map((p) => p.slug));
  const usedSlugs = new Set(
    spots.map((s) => s.placementSlug).filter((s): s is string => !!s),
  );
  return (config.upsells ?? []).filter((u) => {
    if (u.appliesTo !== "order" || SLEEVE_OWNED_KINDS.has(u.kind) || isSleeveEmojiKind(u.kind)) return false;
    const req = (u as { requiresPlacementSlug?: string | null })
      .requiresPlacementSlug;
    if (req && (!presentSlugs.has(req) || usedSlugs.has(req))) return false;
    return true;
  });
}

export function OrderAddOnsControls({
  config,
  upsells,
  pickedIds,
  onToggle,
}: {
  config: Config;
  /** Pre-filtered eligible order add-ons (see eligibleOrderUpsells). */
  upsells: Upsell[];
  pickedIds: string[];
  onToggle: (upsellId: string) => void;
}) {
  if (upsells.length === 0) return null;

  const picked = new Set(pickedIds);
  const pickedCount = upsells.filter((u) => picked.has(u.id)).length;
  // Rush is an "order" add-on, but the builder runs once per garment, so a
  // picked rush only speeds up THIS garment's cart line. In a multi-item
  // order that reads as "my whole order is rushed" when it isn't - so warn
  // the moment it's selected. (Real case RK24062: customer rushed 1 of 3.)
  const rushUpsell = upsells.find((u) => u.kind === "rush");
  const rushPicked = rushUpsell ? picked.has(rushUpsell.id) : false;

  return (
    <section className={`rk-pc-sec${pickedCount > 0 ? " is-active" : ""}`}>
      <div className="rk-pc-sec__head" role="heading" aria-level={2}>
        <span className="rk-pc-sec__num" aria-hidden>
          +
        </span>
        <span className="rk-pc-sec__heading">
          <span className="rk-pc-sec__title">
            {flowCopy(config, "addonsTitle", "Add to your order")}
          </span>
          <span className="rk-pc-sec__summary">
            {pickedCount > 0
              ? `${pickedCount} add-on${pickedCount === 1 ? "" : "s"} added`
              : `${upsells.length} optional extra${upsells.length === 1 ? "" : "s"}`}
          </span>
        </span>
      </div>
      <div className="rk-pc-sec__body">
        <div className="rk-pc-order-addons">
          {upsells.map((u) => {
            const isPicked = picked.has(u.id);
            return (
              <button
                key={u.id}
                type="button"
                className={`rk-pc-order-addons__tile${isPicked ? " is-selected" : ""}`}
                onClick={() => onToggle(u.id)}
                style={
                  u.accent
                    ? ({ ["--rk-pc-upsell-accent" as never]: u.accent } as React.CSSProperties)
                    : undefined
                }
              >
                <span className="rk-pc-order-addons__check" aria-hidden="true">
                  {isPicked ? "✓" : "+"}
                </span>
                <span className="rk-pc-order-addons__body">
                  <span className="rk-pc-order-addons__title">{u.label}</span>
                  {u.hint && (
                    <span className="rk-pc-order-addons__hint">{u.hint}</span>
                  )}
                </span>
                <span className="rk-pc-order-addons__price">
                  +${(u.priceCents / 100).toFixed(0)}
                </span>
              </button>
            );
          })}
        </div>
        {rushPicked && (
          <p className="rk-pc-order-addons__rush-note" role="note">
            <strong>Heads up:</strong> Rush production only applies to this
            product, so it may be sent separately to the other items in your
            order.
          </p>
        )}
      </div>
    </section>
  );
}
